import React, { useState, useEffect, useRef } from 'react';
import { Modal, Table, InputNumber, Button, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { TTBag } from '../graphql/mutations/createTTBag';
import { PRINT_SELECTED_TAGS, PrintSelectedTagsResponse, BagPrintItem } from '../graphql/mutations/batchOperations';
import { printPdfFromBase64 } from '../utils/printUtils';
import { useTranslation } from 'react-i18next';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from '../graphql/queries/getAllCarrierOptions';
import { getCarrierName } from '../utils/carrierUtils';

interface PrintTTTagsModalProps {
    visible: boolean;
    onClose: () => void;
    bags: TTBag[];
    initialPrintCopies?: number;
}

const PrintTTTagsModal: React.FC<PrintTTTagsModalProps> = ({ visible, onClose, bags, initialPrintCopies = 1 }) => {
    const [printCopies, setPrintCopies] = useState<{ [key: string]: number }>({});
    const [printSelectedTags, { loading: printing }] = useMutation<PrintSelectedTagsResponse>(PRINT_SELECTED_TAGS);
    const { t } = useTranslation();
    const submittingRef = useRef(false);

    const { data: carrierData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierData?.getAllCarrierOptions || [];

    useEffect(() => {
        if (visible) {
            const initialCopies = bags.reduce((acc, bag) => {
                acc[bag.id] = initialPrintCopies;
                return acc;
            }, {} as { [key: string]: number });
            setPrintCopies(initialCopies);
        }
    }, [visible, bags, initialPrintCopies]);

    const handleCopiesChange = (bagId: string, copies: number | null) => {
        if (copies !== null) {
            setPrintCopies(prev => ({ ...prev, [bagId]: copies }));
        }
    };

    const handlePrint = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;

        const items: BagPrintItem[] = Object.entries(printCopies)
            .map(([bagId, copies]) => ({ bagId, copies }));

        if (items.length === 0) {
            message.warning(t('wms.bags.messages.noBagsToPrint'));
            submittingRef.current = false;
            return;
        }

        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const localTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const result = await printSelectedTags({
                variables: { items, localTime, timeZone }
            });

            if (result.data?.printSelectedTags?.success && result.data.printSelectedTags.pdfBase64) {
                printPdfFromBase64(result.data.printSelectedTags.pdfBase64, `tt_bag_tags_${Date.now()}.pdf`);
                message.success(t('wms.bags.messages.labelsGeneratedSuccessfully'));
                onClose();
            } else {
                message.error(result.data?.printSelectedTags?.message || t('wms.bags.messages.failedToGenerateLabels'));
            }
        } catch (error) {
            console.error('TT bag printing failed:', error);
            message.error(t('wms.bags.messages.failedToCreateTags'));
        } finally {
            submittingRef.current = false;
        }
    };

    const columns = [
        {
            title: t('wms.bags.pageDetails.bagNumber'),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
        },
        {
            title: t('wms.bags.ttBag.from'),
            dataIndex: 'fromLocation',
            key: 'fromLocation',
            render: (v: string) => v || '-',
        },
        {
            title: t('wms.bags.ttBag.to'),
            dataIndex: 'toLocation',
            key: 'toLocation',
            render: (v: string) => v || '-',
        },
        {
            title: t('wms.bags.ttBag.nextStop'),
            dataIndex: 'nextStop',
            key: 'nextStop',
            render: (v: string) => v || '-',
        },
        {
            title: t('wms.bags.pageDetails.carrier'),
            dataIndex: 'carrierCode',
            key: 'carrierCode',
            render: (carrierCode: number) => getCarrierName(carrierCode, carrierOptions) || '-',
        },
        {
            title: t('wms.bags.pageDetails.printCopies'),
            key: 'copies',
            render: (_: unknown, record: TTBag) => (
                <InputNumber
                    min={1}
                    value={printCopies[record.id] || 1}
                    onChange={(value) => handleCopiesChange(record.id, value)}
                />
            ),
        },
    ];

    return (
        <Modal
            title={t('wms.bags.ttBag.printTitle')}
            visible={visible}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    {t('components.button.cancel')}
                </Button>,
                <Button key="submit" type="primary" loading={printing} onClick={handlePrint}>
                    {t('components.button.print')}
                </Button>,
            ]}
            width={900}
        >
            <Table
                dataSource={bags}
                columns={columns}
                rowKey="id"
                pagination={false}
                className="mb-4"
            />
        </Modal>
    );
};

export default PrintTTTagsModal;
